// apps/web/src/components/NavLevel/NavLevel.tsx
// ─────────────────────────────────────────────────────────────────────────
// Compact LEVEL surface for the top nav. Distinct from the gem chip so users
// learn the difference visually: this is learning progression (XP → Level),
// the gem chip is the cosmetic economy.
//
// Shows: "Lv N · Label" with a thin XP progress bar. Clicking opens a small
// popover with the XP-into-level / next-level numbers, a one-line "what is
// XP" explainer, and a link to the full Level Ladder on the Progress page.
// Floating surface uses hardcoded hex (NOT var()) per the known CSS-Modules
// :local{} cascade bug for popovers.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
	selectLevel,
	selectLevelLabel,
	selectXpIntoLevel,
	selectXpForNextLevel,
	selectXpProgressPct,
	selectTotalXp,
	selectXpReady,
} from '../../store/xpSlice';
import styles from './NavLevel.module.scss';
import { useEscapeKey, useOutsideClick } from '../../hooks';

const NavLevel: React.FC = () => {
	const ready = useSelector(selectXpReady);
	const level = useSelector(selectLevel);
	const label = useSelector(selectLevelLabel);
	const xpInto = useSelector(selectXpIntoLevel);
	const xpForNext = useSelector(selectXpForNextLevel);
	const pct = useSelector(selectXpProgressPct);
	const totalXp = useSelector(selectTotalXp);

	const [open, setOpen] = useState(false);
	const [pulsing, setPulsing] = useState(false);
	const prevLevel = useRef(level);
	const wrapRef = useRef<HTMLDivElement>(null);

	// Celebrate on level increase (quick pulse on the chip).
	useEffect(() => {
		if (level > prevLevel.current) {
			setPulsing(true);
			const t = setTimeout(() => setPulsing(false), 900);
			prevLevel.current = level;
			return () => clearTimeout(t);
		}
		prevLevel.current = level;
		return undefined;
	}, [level]);

	// Close on outside click / Escape (shared overlay hooks).
	useOutsideClick(wrapRef, () => setOpen(false), open);
	useEscapeKey(() => setOpen(false), open);

	if (!ready) return null;

	return (
		<div
			className={styles.wrapper}
			ref={wrapRef}>
			<button
				type='button'
				className={`${styles.chip} ${pulsing ? styles.chipPulsing : ''} ${
					open ? styles.chipOpen : ''
				}`}
				onClick={() => setOpen((o) => !o)}
				aria-haspopup='dialog'
				aria-expanded={open}
				aria-label={`Level ${level}, ${label}. ${xpInto} of ${xpForNext} XP to next level. Open level details.`}>
				<span
					className={styles.star}
					aria-hidden='true'>
					<svg
						viewBox='0 0 24 24'
						fill='none'>
						<path
							d='M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z'
							fill='currentColor'
						/>
					</svg>
				</span>
				<span className={styles.chipText}>
					<span className={styles.chipLevel}>Lv {level}</span>
					<span className={styles.chipLabel}>{label}</span>
				</span>
				<span
					className={styles.bar}
					aria-hidden='true'>
					<span
						className={styles.barFill}
						style={{ width: `${pct}%` }}
					/>
				</span>
			</button>

			{open && (
				<div
					className={styles.popover}
					role='dialog'
					aria-label='Level'>
					<div className={styles.popHead}>
						<span className={styles.popLevel}>Lv {level}</span>
						<span className={styles.popLabel}>{label}</span>
					</div>

					<div className={styles.popBar}>
						<div
							className={styles.popBarFill}
							style={{ width: `${pct}%` }}
						/>
					</div>
					<div className={styles.popRow}>
						<span>{xpInto} XP this level</span>
						<span>{Math.max(0, xpForNext - xpInto)} to go</span>
					</div>

					<div className={styles.popDivider} />

					<div className={styles.popTotal}>
						<span className={styles.popTotalLabel}>Total XP</span>
						<span className={styles.popTotalValue}>
							{totalXp.toLocaleString()}
						</span>
					</div>

					<p className={styles.popNote}>
						XP is your learning level. Read lessons, pass quizzes,
						keep your Memory Anchors solid, and clear your Daily
						Review to level up.
					</p>

					<Link
						to='/progress#levels'
						className={styles.popLadderLink}
						onClick={() => setOpen(false)}>
						See all levels →
					</Link>
				</div>
			)}
		</div>
	);
};

export default NavLevel;
